/**
 * Market name → Korean title resolver
 * 공용: 베팅카트 / 해외형 상세 / 국내형 등
 */
const MKT_MAP = {
  // Code keys
  'MATCH_RESULT': '승무패', '1X2': '승무패', 'MONEY_LINE': '승패',
  'HANDICAP': '핸디캡', 'ASIAN_HANDICAP': '핸디캡',
  'OVER_UNDER': '오버언더', 'TOTALS': '토탈',
  'BTTS': '양팀득점', 'DOUBLE_CHANCE': '더블찬스', 'ODD_EVEN': '홀짝',
  'SPREAD': '스프레드', 'WINNER': '승패',
  'CORRECT_SCORE': '정확한 스코어', 'FIRST_GOAL': '첫 골',
  'HT_RESULT': '전반전', 'HT_FT': '전반전/최종',
  'FIRST_TEAM_TO_SCORE': '첫 득점팀',
  // English name keys (case-insensitive lookup below)
  'Match Result': '승무패', 'Money Line': '승패', 'Winner': '승패',
  'Asian Handicap': '핸디캡', 'Handicap': '핸디캡',
  'Over/Under': '오버언더', 'Under/Over': '오버언더', 'Totals': '토탈',
  'Both Teams To Score': '양팀득점', 'Double Chance': '더블찬스',
  'Odd/Even': '홀짝', 'Spread': '스프레드',
  'First Team to Score': '첫 득점팀', 'First Team To Score': '첫 득점팀',
  'Correct Score': '정확한 스코어', 'First Goal': '첫 골',
  'Half Time Result': '전반전', 'Half Time / Full Time': '전반전/최종',
  // European Handicap
  'European Handicap': '유럽 핸디캡',
  // Period variants
  // 주의: '1st Period' 는 sport 따라 의미가 다름 (soccer 전반전 / basketball 1쿼터 / hockey 1피리어드).
  // sport-aware 정답은 API enrich(effective_market_name, mt.name_ko 우선)에서 처리됨.
  // 본 resolver 는 fallback 이라 sport-blind 한 generic 표기('1피리어드')로 통일하고
  // 명백한 soccer 전용 'Halftime' 만 '전반전' 매핑 유지.
  'Asian Handicap 1st Period': '핸디캡 1피리어드', 'Asian Handicap 2nd Period': '핸디캡 2피리어드',
  'Asian Handicap Halftime': '핸디캡 전반전',
  'Asian Handicap Including Overtime': '핸디캡 (연장포함)',
  'Under/Over 1st Period': '오버언더 1피리어드', 'Under/Over 2nd Period': '오버언더 2피리어드',
  'Under/Over Halftime': '오버언더 전반전',
  'Under/Over Including Overtime': '오버언더 (연장포함)',
  'Asian Under/Over': '아시안 오버언더',
  'Asian Under/Over 1st Period': '아시안 오버언더 1피리어드',
  'Asian Under/Over 2nd Period': '아시안 오버언더 2피리어드',
  // Team variants
  'Under/Over - Home Team': '오버언더 (홈팀)', 'Under/Over - Away Team': '오버언더 (원정팀)',
  'Under/Over - Home Team Including Overtime': '오버언더 (홈팀, 연장포함)',
  'Under/Over - Away Team Including Overtime': '오버언더 (원정팀, 연장포함)',
  // Corners / Cards
  'Under/Over Corners': '코너킥 오버언더', 'Total Corners': '코너킥 합계',
  'Asian Under/Over Corners': '아시안 코너킥 오버언더',
  'Corners Handicap': '코너킥 핸디캡',
  'Under/Over Yellow Cards': '옐로카드 오버언더',
  'Under/Over Cards': '카드 오버언더',
  'Asian Handicap Cards': '카드 핸디캡',
  // Stats
  'Under/Over Shots on Target': '유효슈팅 오버언더',
  'Under/Over Total Shots': '슈팅 합계 오버언더',
  'Under/Over Offsides': '오프사이드 오버언더',
  'Under/Over Tackles': '태클 오버언더',
  // Period winners — sport-blind generic
  '1st Period Winner': '1피리어드 승무패', '2nd Period Winner': '2피리어드 승무패',
  '1st Period Winner Home/Away': '1피리어드 승패', '2nd Period Winner Home/Away': '2피리어드 승패',
  '1st Half Winner': '전반전 승무패', '2nd Half Winner': '후반전 승무패',
  // Other common
  '12': '승패', 'Both Teams To Score In Both Halves': '양팀 전후반 득점',
  'Draw No Bet': '무승부 환불', 'Number of Goals': '골수',
  'First Card': '첫 카드', 'Odd/Even Corners': '코너킥 홀짝',
  'Home Win No Bet': '홈승 (무환불)', 'Away Win No Bet': '원정승 (무환불)',
  '10 Minute Result': '10분 결과', '60 Minute Result': '60분 결과', '75 Minute Result': '75분 결과',
};

// Case-insensitive lookup cache
const _lowerMap = {};
for (const [k, v] of Object.entries(MKT_MAP)) {
  _lowerMap[k.toLowerCase()] = v;
}

const hasKorean = (value) => /[\uac00-\ud7a3]/.test(String(value || ''));

const isMachineDisplayValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized.startsWith('live365:')
    || normalized.startsWith('stable:live365:')
    || normalized.startsWith('catalog:')
    || normalized.includes(':selection_group:')
    || normalized.includes('selection_group:')
    || normalized.includes(':selection:')
    || normalized.includes(':scope:')
    || normalized.includes('contractmarketid')
    || normalized.includes('marketcontractid')
    || normalized.includes('selectioncontractid')
    || normalized.includes('contractselectionid')
    || normalized.includes('provideroddref')
    || /^key:.*::id:/i.test(normalized)
  );
};

const normalizeMarketTitle = (value) => {
  const text = String(value || '').trim().replace(/\s+/g, ' ');
  if (!text || isMachineDisplayValue(text)) return '';
  if (hasKorean(text) && text.includes('_')) {
    const [head, ...tailParts] = text.split('_').map((part) => part.trim()).filter(Boolean);
    const tail = tailParts.join(' ');
    if (head && tail) return `${head} [${tail}]`;
  }
  return text;
};

const compactMarketTitle = (value) => normalizeMarketTitle(value)
  .replace(/\s+/g, '')
  .replace(/[()[\]{}]/g, '')
  .toLowerCase();

const GENERIC_KO_MARKET_TITLES = new Set([
  '마켓',
  '스페셜',
  '승무패',
  '승/무/패',
  '승패',
  '승/패',
  '핸디캡',
  '오버언더',
  '오버/언더',
  '언오버',
  '언더/오버',
  '오/언',
  '언/오',
  '오',
  '언',
  '토탈',
  '스프레드',
]);

const PERIOD_CONTRACT_LABELS = {
  h1: '전반',
  h2: '후반',
  p1: '1피리어드',
  p2: '2피리어드',
  p3: '3피리어드',
  p4: '4피리어드',
  q1: '1쿼터',
  q2: '2쿼터',
  q3: '3쿼터',
  q4: '4쿼터',
  i1: '1이닝',
  i2: '2이닝',
  i3: '3이닝',
  i4: '4이닝',
  i5: '1-5이닝',
  i6: '6이닝',
  i7: '7이닝',
  i8: '8이닝',
  i9: '9이닝',
  s1: '1세트',
  s2: '2세트',
  s3: '3세트',
  s4: '4세트',
  s5: '5세트',
};

const CONTRACT_FAMILY_LABELS = {
  handicap: '핸디캡',
  asian_handicap: '아시안핸디캡',
  total: '오버/언더',
  over_under: '오버/언더',
  asian_total: '아시안 오버/언더',
  team_total: '팀 오버/언더',
  btts: '양팀 득점',
  correct_score: '정확 스코어',
  double_chance: '더블찬스',
  draw_no_bet: '무승부 환불',
  odd_even: '홀짝',
  exact_goals: '정확 골수',
  nth_goal: 'N번째 골',
  nth_corner: '지정번째 코너',
  last_corner: '마지막 코너',
  team_to_score: '득점팀',
};

const parseContractMarketIdentity = (market) => {
  const raw = [
    market?.contract_market_id,
    market?.contractMarketId,
    market?.marketContractId,
    market?.market_type?.contract_market_id,
    market?.marketType?.contract_market_id,
  ].find((value) => value != null && String(value).trim());
  const text = String(raw || '').trim();
  if (!text) return null;
  const match = text.match(/^(?:stable:)?[^:]+:canonical:([^:]+):market:([^:]+)$/i);
  if (!match) return null;
  return {
    sportKey: match[1].toLowerCase(),
    marketKey: match[2].toLowerCase(),
  };
};

const resolveWinnerFamilyLabel = (sportKey) => (
  ['soccer', 'football'].includes(String(sportKey || '').toLowerCase()) ? '승무패' : '승패'
);

const buildContractMarketTitle = (market) => {
  const identity = parseContractMarketIdentity(market);
  if (!identity?.marketKey) return '';
  const periodMatch = identity.marketKey.match(/^period_(.+)_((?:h|p|q|i|s)\d+)$/i);
  const family = periodMatch ? periodMatch[1] : identity.marketKey;
  const periodToken = periodMatch ? periodMatch[2].toLowerCase() : null;
  const familyLabel = family === 'winner'
    ? resolveWinnerFamilyLabel(identity.sportKey)
    : CONTRACT_FAMILY_LABELS[family] || '';
  if (!familyLabel) return '';
  const periodLabel = periodToken ? PERIOD_CONTRACT_LABELS[periodToken] : '풀타임';
  return periodLabel ? `${periodLabel} / ${familyLabel}` : familyLabel;
};

const isGenericKoreanMarketTitle = (value) => {
  if (!hasKorean(value)) return false;
  return GENERIC_KO_MARKET_TITLES.has(compactMarketTitle(value));
};

const firstSpecificKoreanCandidate = (candidates) => (
  candidates
    .map(normalizeMarketTitle)
    .find((value) => hasKorean(value) && !isGenericKoreanMarketTitle(value))
);

const translateSpecificCandidate = (candidates) => {
  for (const value of candidates) {
    if (isFeedCode(value)) continue;
    const translated = normalizeMarketTitle(getMarketTitle(value));
    if (translated && translated !== value && !isGenericKoreanMarketTitle(translated)) {
      return translated;
    }
  }
  return '';
};

export function getMarketTitle(name) {
  if (!name) return name;
  if (isMachineDisplayValue(name)) return '';
  // Already Korean?
  if (hasKorean(name)) return normalizeMarketTitle(name);
  // Direct match
  if (MKT_MAP[name]) return MKT_MAP[name];
  // Case-insensitive
  const lower = name.toLowerCase();
  if (_lowerMap[lower]) return _lowerMap[lower];
  // Prefix-based fallback for period/team/half variants
  const _prefixMap = [
    [/^match result/i, '승무패'],
    [/^1x2\b/i, '승무패'],
    [/^12\b/i, '승패'],
    [/^money line\b/i, '승패'],
    [/^winner\b/i, '승패'],
    [/^european handicap/i, '유럽 핸디캡'],
    [/^asian handicap/i, '핸디캡'],
    [/^asian under\/over/i, '아시안 오버언더'],
    [/^under\/over.*corner/i, '코너킥 오버언더'],
    [/^under\/over.*card/i, '카드 오버언더'],
    [/^under\/over.*yellow/i, '옐로카드 오버언더'],
    [/^under\/over.*shot/i, '슈팅 오버언더'],
    [/^under\/over.*offside/i, '오프사이드 오버언더'],
    [/^under\/over.*tackle/i, '태클 오버언더'],
    [/^under\/over/i, '오버언더'],
    [/^corners handicap/i, '코너킥 핸디캡'],
    [/^double chance/i, '더블찬스'],
    [/^both teams to score/i, '양팀득점'],
    [/^correct score/i, '정확한 스코어'],
    [/^number of goals/i, '골수'],
    [/^first team to score/i, '첫 득점팀'],
  ];
  for (const [re, ko] of _prefixMap) {
    if (re.test(name)) {
      // Append period/team suffix if present
      // 주의: '1st Period' 는 sport 따라 의미가 다르므로 generic '피리어드' 로 통일.
      // soccer 전용 'Halftime' / '1st Half' 만 '전반전' 으로 변환.
      const suffix = name.replace(re, '').trim();
      if (!suffix) return ko;
      const sfx = suffix
        .replace(/1st Half/i, '전반전').replace(/2nd Half/i, '후반전')
        .replace(/1st Period/i, '1피리어드').replace(/2nd Period/i, '2피리어드')
        .replace(/3rd Period/i, '3피리어드').replace(/4th Period/i, '4피리어드')
        .replace(/Home Team/i, '홈팀').replace(/Away Team/i, '원정팀')
        .replace(/Including Overtime/i, '연장포함').replace(/Halftime/i, '전반전');
      return normalizeMarketTitle(`${ko} ${sfx}`.trim());
    }
  }
  return normalizeMarketTitle(name);
}

export default getMarketTitle;


/** Feed-like code detector — only actual feed prefixes */
export function isFeedCode(val) {
  if (!val) return false;
  return /^feed[_\-]/i.test(val);
}

const appendLineScoreContext = (label, market) => {
  const title = normalizeMarketTitle(label);
  const context = market?.line_score_context || market?.lineScoreContext || null;
  const scoreLabel = typeof context === 'string'
    ? context
    : context && typeof context === 'object'
      ? (context.label || (Number.isFinite(Number(context.home)) && Number.isFinite(Number(context.away)) ? `${Number(context.home)}-${Number(context.away)}` : ''))
      : '';
  if (!title || !scoreLabel) return title;
  const lineLabel = normalizeMarketTitle(market?.display_line || market?.base_line || market?.line || market?.line_value || '');
  const lineAwareTitle = lineLabel && !title.includes(lineLabel) ? `${title} ${lineLabel}` : title;
  return lineAwareTitle.includes(`(${scoreLabel})`) ? lineAwareTitle : `${lineAwareTitle} (${scoreLabel})`;
};

/** Build display-safe market title from market object */
export function buildMarketDisplayTitle(market) {
  if (!market) return '';
  const finish = (value) => appendLineScoreContext(value, market);
  const v2DisplayTitle = firstSpecificKoreanCandidate([
    market.marketDisplayNameKo,
    market.market_display_name_ko,
    market.v2_display_model?.marketDisplayNameKo,
    market.v2_display_model?.market_display_name_ko,
  ]);
  if (v2DisplayTitle) return finish(v2DisplayTitle);

  const contractTitle = buildContractMarketTitle(market);
  if (contractTitle) return finish(contractTitle);

  const koCandidates = [
    market.market_title_display_ko,
    market.effective_market_name_ko,
    market.market_title_display,
    market.market_name_ko,
    market.name_ko,
    market.market_display_label,
    market.market_meaning_label,
    market.market_type_name_ko,
    market.market_type?.name_ko,
  ].filter(Boolean);
  const enCandidates = [
    market.effective_market_name_en,
    market.effective_market_name,
    market.market_name,
    market.name,
    market.market_type_name,
    market.market_type?.name,
    market.market_code,
  ].filter(Boolean);

  const firstKoCandidate = koCandidates
    .map(normalizeMarketTitle)
    .find((value) => hasKorean(value) && !isGenericKoreanMarketTitle(value));
  if (firstKoCandidate) return finish(firstKoCandidate);

  const rowCandidates = [
    market.market_name_ko,
    market.market_name,
    market.name_ko,
    market.name,
  ].filter(Boolean);
  const typeCandidates = [
    market.market_type_name_ko,
    market.market_type?.name_ko,
    market.market_type_name,
    market.market_type?.name,
    market.market_code,
  ].filter(Boolean);

  const rowSpecific = firstSpecificKoreanCandidate(rowCandidates);
  if (rowSpecific) return finish(rowSpecific);

  const typeSpecific = firstSpecificKoreanCandidate(typeCandidates);
  if (typeSpecific) return finish(typeSpecific);

  const rowReadableCandidates = rowCandidates.filter((value) => !isFeedCode(value));
  const rowSpecificTranslated = translateSpecificCandidate(rowReadableCandidates);
  if (rowSpecificTranslated) return finish(rowSpecificTranslated);

  const typeReadableCandidates = typeCandidates.filter((value) => !isFeedCode(value));
  const typeSpecificTranslated = translateSpecificCandidate(typeReadableCandidates);
  if (typeSpecificTranslated) return finish(typeSpecificTranslated);

  const firstKoreanRowCandidate = rowCandidates
    .map(normalizeMarketTitle)
    .find(hasKorean);
  if (firstKoreanRowCandidate) return finish(firstKoreanRowCandidate);

  const firstKoreanTypeCandidate = typeCandidates
    .map(normalizeMarketTitle)
    .find(hasKorean);
  if (firstKoreanTypeCandidate) return finish(firstKoreanTypeCandidate);

  // 2) Type fallback — still exclude feed codes only
  for (const value of enCandidates.filter((candidate) => !isFeedCode(candidate))) {
    const translated = getMarketTitle(value);
    if (translated && translated !== value) return finish(translated);
  }
  for (const value of typeReadableCandidates) {
    const translated = getMarketTitle(value);
    if (translated !== value) return finish(translated);
  }
  if (enCandidates.length > 0) return finish(normalizeMarketTitle(enCandidates[0]));
  if (rowReadableCandidates.length > 0) return finish(getMarketTitle(rowReadableCandidates[0]));
  if (typeReadableCandidates.length > 0) return finish(getMarketTitle(typeReadableCandidates[0]));

  return '';
}
